import { useEffect, useMemo, useState } from "react";
import { confirmReviewExport, createReviewExportPreview } from "../application/round02-service";
import {
  applyPromiseAction,
  captureIdea,
  createPromise,
  saveFocusDeviationFromReview,
  saveReviewStatementToTruthInbox,
} from "../application/round03-service";
import type { CEOOSDatabase } from "../db/database";
import type {
  BehaviorCorrection,
  CEOProfile,
  DailyCEOReview,
  EvidenceStatement,
  EvidenceStatementType,
  PromiseEntry,
} from "../domain/types";
import { reviewIssueLabel } from "../domain/review-labels";
import type { ReviewDeviationField } from "../import-export/board-export";
import { downloadJson } from "../import-export/file";
import { safeErrorMessage } from "../privacy/policy";
import { BackIcon } from "../ui/icons";
import type { GovernanceArea } from "../ui/GovernanceTabs";

const FIELD_LABELS: Record<ReviewDeviationField, string> = {
  uncomfortable_truth: "不舒服事实",
  focus_deviation: "聚焦偏差",
  repetition_failure: "重复动作缺口",
  trust_deviation: "信任偏差",
  decision_quality_issue: "决策质量问题",
  delegation_issue: "组织与授权问题",
  risk_issue: "风险问题",
  energy_issue: "能量问题",
  field_adaptation_issue: "场域适配问题",
};

export function ReviewCompletion({
  database,
  review,
  correction,
  profile,
  onBack,
  onExported,
  onOpenGovernance,
}: {
  database: CEOOSDatabase;
  review: DailyCEOReview;
  correction: BehaviorCorrection;
  profile: CEOProfile;
  onBack: () => void;
  onExported: () => Promise<void>;
  onOpenGovernance: (area: GovernanceArea) => void;
}) {
  const availableFields = useMemo(
    () =>
      Object.entries(review.exportFieldSummaries ?? {})
        .filter((entry): entry is [ReviewDeviationField, string] => Boolean(entry[1]))
        .map(([field]) => field),
    [review.exportFieldSummaries],
  );
  const [included, setIncluded] = useState<ReviewDeviationField[]>(availableFields);
  const [preview, setPreview] = useState("");
  const [notice, setNotice] = useState("");
  const [exporting, setExporting] = useState(false);
  const [reviewStatements, setReviewStatements] = useState<EvidenceStatement[]>([]);
  const [promises, setPromises] = useState<PromiseEntry[]>([]);
  const [selectedPromiseId, setSelectedPromiseId] = useState("");
  const [trustNote, setTrustNote] = useState("");
  const [promiseAlias, setPromiseAlias] = useState("");
  const [openedAtMs] = useState(() => Date.now());
  const [promiseDueAt, setPromiseDueAt] = useState(() => {
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
    const offset = tomorrow.getTimezoneOffset() * 60_000;
    return new Date(tomorrow.getTime() - offset).toISOString().slice(0, 16);
  });

  useEffect(() => {
    void createReviewExportPreview(database, review, included)
      .then(({ envelope }) => setPreview(JSON.stringify(envelope, null, 2)))
      .catch((error) => setNotice(safeErrorMessage(error)));
  }, [database, included, review]);

  useEffect(() => {
    void Promise.all([
      database.structuredRecords.bulkGet(review.statementIds),
      database.structuredRecords.where("recordType").equals("promise").toArray(),
    ]).then(([records, promiseRecords]) => {
      setReviewStatements(
        records.filter(
          (record): record is EvidenceStatement => record?.recordType === "evidence_statement",
        ),
      );
      setPromises(
        promiseRecords.filter(
          (record): record is PromiseEntry =>
            record.recordType === "promise" &&
            record.deletionState === "active" &&
            !["fulfilled", "cancelled", "breached", "archived"].includes(record.promiseStatus),
        ),
      );
    });
  }, [database, review.id, review.statementIds]);

  const saveTruth = async (targetType: EvidenceStatementType) => {
    const source =
      reviewStatements.find(
        (statement) => statement.content === review.exportFieldSummaries?.uncomfortable_truth,
      ) ?? reviewStatements[0];
    if (!source) {
      setNotice("本次复盘没有可追溯的结构化陈述；未创建 Truth 项。");
      return;
    }
    try {
      await saveReviewStatementToTruthInbox(database, review, source.id, targetType);
      await onExported();
      setNotice("已按你的明确选择保存到 Truth Inbox；复盘原记录保持不变。");
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  const saveFocusDeviation = async () => {
    try {
      await saveFocusDeviationFromReview(database, review, correction.action);
      await onExported();
      setNotice("聚焦偏差已保存并关联本次 review_id。");
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  const parkReviewIdea = async () => {
    const source = review.exportFieldSummaries?.focus_deviation?.trim();
    if (!source) return;
    try {
      await captureIdea(database, {
        title: source.slice(0, 80),
        description: source,
        effectiveDate: review.reviewDate,
        coolingHours: profile.ideaCoolingHours,
        captureSource: "daily_review",
        sourceRecordIds: [review.id],
      });
      await onExported();
      setNotice("复盘中的新想法已停放；没有创建项目或任务。");
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  const updateExistingPromise = async (type: "fulfill" | "request_renegotiation") => {
    if (!selectedPromiseId) return;
    try {
      const result = await applyPromiseAction(
        database,
        selectedPromiseId,
        type === "fulfill"
          ? { type: "fulfill", evidence: trustNote }
          : { type: "request_renegotiation", note: trustNote },
      );
      setNotice(
        result.issues.length
          ? result.issues.map((item) => `${item.code}：${item.message}`).join("；")
          : "已有承诺已更新；没有重复创建。",
      );
      if (!result.issues.some((item) => item.severity === "blocking")) {
        await onExported();
        setPromises((current) =>
          current.map((item) => (item.id === result.promise.id ? result.promise : item)),
        );
      }
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  const createReviewPromise = async () => {
    const content = review.exportFieldSummaries?.trust_deviation?.trim();
    if (!content || !promiseAlias.trim()) return;
    try {
      const promisedAt = new Date().toISOString();
      await createPromise(database, {
        effectiveDate: review.reviewDate,
        counterpartyType: "other",
        counterpartyAlias: promiseAlias.trim(),
        content,
        promisedAt,
        dueAt: new Date(promiseDueAt).toISOString(),
        businessTimezone: profile.businessTimezone,
        materiality: "medium",
        trustImpact: trustNote.trim() || "本次复盘识别的信任影响。",
        promiseSource: "daily_review",
        sourceRecordIds: [review.id],
      });
      await onExported();
      setNotice("最小承诺已保存并关联本次复盘；重复点击不会重复创建。");
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  const exportReview = async () => {
    setExporting(true);
    try {
      const envelope = await confirmReviewExport(database, review, included);
      if (JSON.stringify(envelope).includes("rawText")) {
        throw new Error("CEO-EXPORT-PRIVACY: 导出被隐私守卫阻止。");
      }
      downloadJson(envelope, `ceo-review-${review.reviewDate}.json`);
      setNotice("董事会摘要已生成并下载；应用无法远程召回该文件。");
      await onExported();
    } catch (error) {
      setNotice(safeErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };

  return (
    <main className="completion-page">
      <header className="review-topbar">
        <button type="button" className="back-button" onClick={onBack}>
          <BackIcon />
          今天
        </button>
        <h1>复盘结果</h1>
        <span />
      </header>

      {notice && (
        <div className="inline-notice" role="status">
          {notice}
        </div>
      )}

      <section className="completion-summary">
        <div>
          <span>今天最大的个人偏差</span>
          <p>{review.founderDeviation}</p>
        </div>
        <div>
          <span>受影响业务结果</span>
          <p>{review.affectedBusinessResult}</p>
        </div>
        <div>
          <span>问题类型</span>
          <p>{reviewIssueLabel(review.issueType)}</p>
        </div>
        <div className="correction-callout">
          <span>明日唯一行为纠偏</span>
          <p>{correction.action}</p>
          <small>验证：{correction.verificationMetric}</small>
        </div>
      </section>

      <section className="review-governance-actions" aria-labelledby="optional-governance">
        <h2 id="optional-governance">可选：沉淀为长期治理记录</h2>
        <p className="section-helper">这些动作不会阻挡复盘完成；不保存也是有效选择。</p>

        {review.exportFieldSummaries?.uncomfortable_truth && (
          <details className="governance-disclosure">
            <summary>不舒服事实 → Truth Inbox</summary>
            <p>{review.exportFieldSummaries.uncomfortable_truth}</p>
            <div className="segmented-actions">
              <button type="button" onClick={() => void saveTruth("fact")}>
                保存为事实审计项
              </button>
              <button type="button" onClick={() => void saveTruth("hypothesis")}>
                保存为待验证假设
              </button>
              <button type="button" onClick={() => void saveTruth("recommendation")}>
                保存为建议
              </button>
              <button type="button" onClick={() => onOpenGovernance("truth")}>
                查看 Truth Audit
              </button>
            </div>
          </details>
        )}

        {review.exportFieldSummaries?.focus_deviation && (
          <details className="governance-disclosure">
            <summary>聚焦偏差 → Focus Guard</summary>
            <p>{review.exportFieldSummaries.focus_deviation}</p>
            <div className="segmented-actions">
              <button type="button" onClick={() => void saveFocusDeviation()}>
                保存聚焦偏差
              </button>
              <button type="button" onClick={() => void parkReviewIdea()}>
                作为想法停放
              </button>
              <button type="button" onClick={() => onOpenGovernance("focus")}>
                查看 Focus Guard
              </button>
            </div>
          </details>
        )}

        {review.exportFieldSummaries?.trust_deviation && (
          <details className="governance-disclosure">
            <summary>信任偏差 → Promise Ledger</summary>
            <p>{review.exportFieldSummaries.trust_deviation}</p>
            {promises.length > 0 && (
              <>
                <label className="field">
                  <span>选择一个已有承诺（可选）</span>
                  <select
                    value={selectedPromiseId}
                    onChange={(event) => setSelectedPromiseId(event.target.value)}
                  >
                    <option value="">暂不关联</option>
                    {promises.map((promise) => (
                      <option value={promise.id} key={promise.id}>
                        {promise.counterpartyAlias} · {promise.content}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>兑现证据或提前重谈原因</span>
                  <input value={trustNote} onChange={(event) => setTrustNote(event.target.value)} />
                </label>
                <div className="segmented-actions">
                  <button
                    type="button"
                    disabled={!selectedPromiseId}
                    onClick={() => void updateExistingPromise("fulfill")}
                  >
                    标记已兑现
                  </button>
                  <button
                    type="button"
                    disabled={!selectedPromiseId || !trustNote.trim()}
                    onClick={() => void updateExistingPromise("request_renegotiation")}
                  >
                    标记需要提前重谈
                  </button>
                </div>
              </>
            )}
            <div className="two-column-fields">
              <label className="field">
                <span>新承诺对手方别名</span>
                <input
                  value={promiseAlias}
                  onChange={(event) => setPromiseAlias(event.target.value)}
                />
              </label>
              <label className="field">
                <span>截止时间</span>
                <input
                  type="datetime-local"
                  value={promiseDueAt}
                  onChange={(event) => setPromiseDueAt(event.target.value)}
                />
              </label>
            </div>
            <div className="segmented-actions">
              <button
                type="button"
                disabled={
                  !promiseAlias.trim() ||
                  !promiseDueAt ||
                  new Date(promiseDueAt).getTime() <= openedAtMs
                }
                onClick={() => void createReviewPromise()}
              >
                创建最小 PromiseEntry
              </button>
              <button type="button" onClick={() => onOpenGovernance("promise")}>
                查看 Promise Ledger
              </button>
            </div>
          </details>
        )}
      </section>

      <section className="export-section" aria-labelledby="export-preview">
        <h2 id="export-preview">CEOReviewExport 预览</h2>
        <p className="section-helper">
          只投影已经确认的结构化摘要。取消任何非必要字段后再生成文件。
        </p>
        <div className="export-field-list">
          {availableFields.map((field) => (
            <label className="check-row" key={field}>
              <input
                type="checkbox"
                checked={included.includes(field)}
                onChange={(event) =>
                  setIncluded((current) =>
                    event.target.checked
                      ? [...new Set([...current, field])]
                      : current.filter((item) => item !== field),
                  )
                }
              />
              <span>
                <strong>{FIELD_LABELS[field]}</strong>
                {review.exportFieldSummaries?.[field]}
              </span>
            </label>
          ))}
        </div>
        <details className="disclosure">
          <summary>查看完整合同预览</summary>
          <pre className="contract-preview">{preview}</pre>
        </details>
        <div className="privacy-guard-line">私人原文、未确认推断和未勾选字段不会进入该文件。</div>
        <button
          type="button"
          className="primary-button full-width"
          disabled={exporting}
          onClick={() => void exportReview()}
        >
          {exporting ? "正在生成…" : "确认字段并导出 JSON"}
        </button>
      </section>
    </main>
  );
}
